import { useState, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useDispatch } from 'react-redux'
import LoadingOverlay from 'components/LoadingOverlay'
import * as classroomActions from 'actions/classroom'
import { useHistory } from 'react-router-dom'
import api from 'api'
import ButtonsCardLayout from 'components/ButtonsCardLayout'
import cn from 'classnames'
import { useReaderProvider } from 'context/ReaderContext'
import onebadger from 'assets/onebadger.png'
import twobadgers from 'assets/twobadgers.png'
import threebadgers from 'assets/threebadgers.png'
import styles from './styles.module.scss'
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { NO_POWERTOOLS_LICENSE } from 'utils/defaults'
import { authKeys } from 'utils/config'

const EMPTY_ROSTER_RETRY_MS = 1000

const rosterFromStudentsResponse = (data) => {
  const students = Array.isArray(data?.students) ? data.students : []
  return {
    students,
    teacherID: students[0]?.teacherID || data?.teacher,
  }
}

const StartPage = (props) => {
  const history = useHistory()
  const { getAccessTokenSilently, logout, user } = useAuth0()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)

  const [subscriptionStatus, setSubscriptionStatus] = useState(null)
  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);

  const {
    setReading,
    setBuddyMode,
    setIndividualMode,
    setGroupMode,
    setMode
  } = useReaderProvider()

  const [animating, setAnimating] = useState(false)
  const [singleStudent, setSingleStudent] = useState(false)

  const modes = [
    {
      name: 'Individual',
      image: onebadger,
      modeSetter: setIndividualMode,
      redirectTo: '/get-started',
    },
    {
      name: 'Buddy',
      image: twobadgers,
      modeSetter: setBuddyMode,
      redirectTo: '/get-started',
    },
    {
      name: 'Group',
      image: threebadgers,
      modeSetter: setGroupMode,
      redirectTo: '/pick-level',
    },
  ]

  useEffect(() => {
    setReading(false)
    let cancelled = false

    const loadClassroom = async ({ retryEmpty = true } = {}) => {
      const token = await getAccessTokenSilently({
        authorizationParams: authKeys.authorizationParams,
      })
      const { data: subscription } = await api.get('/api/check/subscription', {
        headers: { authorization: `Bearer ${token}` },
      })
      if (cancelled) return

      console.log('subscription', subscription.status)
      if (['NOTSUBSCRIBED', 'EXPIRED'].includes(subscription.status)) {
        setSubscriptionStatus(subscription.status)
        setModal(true)
      }

      const { data } = await api.get('/api/get/students', {
        headers: { authorization: `Bearer ${token}` },
      })
      if (cancelled) return

      const { students, teacherID } = rosterFromStudentsResponse(data)
      if (students.length === 0 && retryEmpty) {
        await new Promise((resolve) => setTimeout(resolve, EMPTY_ROSTER_RETRY_MS))
        if (cancelled) return
        return loadClassroom({ retryEmpty: false })
      }

      setSingleStudent(students.length <= 1)
      dispatch(classroomActions.setStudents(students))
      if (!teacherID) {
        setLoading(false)
        return
      }

      dispatch(classroomActions.setTeacher({ id: teacherID }))
      try {
        const r = await api.get(`/api/get/themes/${teacherID}`, {
          headers: { authorization: `Bearer ${token}` },
        })
        if (!cancelled) {
          dispatch(classroomActions.setThemes(r.data?.reading_levels))
        }
      } catch (err) {
        console.log(`Error grabbing themes >>> ${err}`)
      }
      if (!cancelled) setLoading(false)
    }

    loadClassroom().catch((err) => {
      if (err && !cancelled) {
        console.error('Failed to load classroom data:', err.message || err)
        setLoading(false)
      }
    })

    return () => {
      cancelled = true
    }
  }, [dispatch, getAccessTokenSilently, setReading])

  const closeAndLogout = () => {

    toggle()
    logout({ logoutParams: { returnTo: window.location.origin } })
    history.push('/login')
  }

  const closeBtn = (
    <button className="close" onClick={closeAndLogout} type="button">
      &times;
    </button>
  );

  return (
    <LoadingOverlay loading={loading}>
      { subscriptionStatus && 
        <Modal isOpen={modal} toggle={toggle} backdrop='static'>
          <ModalHeader toggle={toggle} close={closeBtn}>{NO_POWERTOOLS_LICENSE[subscriptionStatus]["header"]}</ModalHeader>
          <ModalBody>
            <div className='row'>
              <div className='col-12'>
                {NO_POWERTOOLS_LICENSE[subscriptionStatus]["body"]}
              </div>
              <div className='col-12 py-3'>
                <a className="btn btn-danger btn-block" href="mailto:info@toolsofthemind.org">Contact Support</a>
              </div>
            </div>
          </ModalBody>
        </Modal>
      }
      <ButtonsCardLayout animating={animating}>
        <ButtonsCardLayout.Body>
          {modes.map(({ modeSetter, name, image, redirectTo }, index) => (
            <ButtonsCardLayout.Button
              beforeRedirect={(evt) => {
                setAnimating(true)
                setTimeout(() => {
                  modeSetter(evt)
                  setMode(name)
                  setAnimating(false)
                  history.push(redirectTo)
                }, 800)
              }}
              disabled={singleStudent && name === 'Buddy' ? true : false}
              contentLeft={<img src={image} alt={name} />}
              contentCenter={`${name} mode`}
              className="blue text-white"
              key={name}
            />
          ))}
        </ButtonsCardLayout.Body>
        <div className={cn(styles.footerText, 'text-muted')}>
          Logged in as: {user?.nickname || user?.name || user?.email || 'Unknown user'}
        </div>
      </ButtonsCardLayout>
    </LoadingOverlay>
  )
}

export default StartPage
